import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import { 
  Users, Clock, Calendar, Plus, Edit2, Trash2, Download, 
  Eye, FileText, Search, RotateCcw, RotateCw,
  AlertTriangle, CheckCircle, ClipboardList, Grid3X3
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "@/components/Header";
import AdminSidebar from "@/components/Sidebar";
import { db, auth } from "../../firebase/config";
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  startAt, 
  endAt,  
  getCountFromServer,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  addDoc
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";

interface Teacher {
  id: string;
  fullName: string;
  shortName: string;
  staffId?: string;
  department?: string;
  availability: 'available' | 'unavailable' | 'preferred-off';
  preferredDaysOff: string[];
  dutyCount: number;
}

interface DutyType {
  id: string;
  name: string;
  description: string;
  requiredTeachers: number;
  applicableDays: string[];
  frequency: 'daily' | 'weekly' | 'monthly';
  hasShifts: boolean;
}

interface DutyAssignment {
  dutyId: string;
  day: string;
  date?: string;
  teacherIds: string[];
  notes?: string;
  shift?: 'day' | 'night';
}

interface Term {
  id: string;
  number: number;
  title: string;
  openingDate: Date | undefined;
  closingDate: Date | undefined;
}

interface Roster {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  format: 'weekly' | 'daily';
  assignments: DutyAssignment[];
  status: 'draft' | 'published';
  createdAt: string;
  termId?: string;
  termName?: string;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DutyRosterMaker = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [dutyTypes, setDutyTypes] = useState<DutyType[]>([]);
  const [rosters, setRosters] = useState<Roster[]>([]);
  const [currentRoster, setCurrentRoster] = useState<Roster | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<DutyAssignment[][]>([]);
  const [redoStack, setRedoStack] = useState<DutyAssignment[][]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination State
  const [lastVisible, setLastVisible] = useState<any | null>(null); // Use 'any' or correct Firestore type if imported
  const [pageStack, setPageStack] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const itemsPerPage = 10;

  // Form states
  const [newDuty, setNewDuty] = useState<Partial<DutyType>>({
    requiredTeachers: 1,
    applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    frequency: 'daily',
    hasShifts: false
  });
  const [newRoster, setNewRoster] = useState<Partial<Roster>>({
    format: 'weekly',
    status: 'draft'
  });

  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [editingDuty, setEditingDuty] = useState<DutyType | null>(null);
  const [showDutyDialog, setShowDutyDialog] = useState(false);
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignmentContext, setAssignmentContext] = useState<{dutyId: string; day: string; shift?: 'day' | 'night'} | null>(null);

  // Helper to fetch total count
  const fetchTotalCount = async (schoolId: string, search: string) => {
    try {
      const teachersRef = collection(db, `schools/${schoolId}/teachers`);
      let q = query(teachersRef);
      if (search) {
        q = query(teachersRef, orderBy("firstName"), startAt(search), endAt(search + '\uf8ff'));
      }
      const snapshot = await getCountFromServer(q);
      setTotalTeachers(snapshot.data().count);
    } catch (err) {
      console.error("Error fetching count:", err);
    }
  };

  // Load data from Firebase and localStorage
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const loadTeachers = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const tokenResult = await getIdTokenResult(user);
        const schoolID = tokenResult.claims.schoolID as string;
        setSchoolId(schoolID);

        if (schoolID) {
          const teachersRef = collection(db, `schools/${schoolID}/teachers`);
          let q;

          // Base query for search or full list
          if (searchQuery.trim()) {
            q = query(teachersRef, orderBy("firstName"), startAt(searchQuery), endAt(searchQuery + '\uf8ff'));
          } else {
            q = query(teachersRef, orderBy("firstName", "asc"));
          }

          // Apply Pagination
          if (currentPage > 1 && pageStack.length >= currentPage - 1) {
            const cursor = pageStack[currentPage - 2];
            q = query(q, startAfter(cursor), limit(itemsPerPage));
          } else {
            q = query(q, limit(itemsPerPage));
          }

          unsubscribe = onSnapshot(q, (snapshot) => {
            const teacherData = snapshot.docs.map(doc => {
              const data = doc.data();
              const fullName = `${data.title || ''} ${data.firstName} ${data.lastName}`.trim();
              const shortName = `${data.title || ''} ${data.lastName}`.trim();
              
              return {
                id: doc.id,
                fullName,
                shortName,
                staffId: data.employeeId,
                department: data.department || "General",
                availability: 'available',
                preferredDaysOff: [],
                dutyCount: 0
              } as Teacher;
            });
            setTeachers(teacherData);
            
            if (snapshot.docs.length > 0) {
              setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            } else {
              setLastVisible(null);
            }
          });

          fetchTotalCount(schoolID, searchQuery);
        }
      } catch (error) {
        console.error("Error loading teachers:", error);
        toast({
          title: "Error",
          description: "Failed to load teachers from database",
          variant: "destructive"
        });
      }
    };

    loadTeachers();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [searchQuery, currentPage]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setTeachers([]);
        setCurrentPage(1);
        setPageStack([]);
        setCurrentPage(1);
        setPageStack([]);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!schoolId) return;

    const dutiesRef = collection(db, `schools/${schoolId}/dutyTypes`);
    const rostersRef = collection(db, `schools/${schoolId}/rosters`);

    const unsubscribeDuties = onSnapshot(dutiesRef, (snapshot) => {
      const duties = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DutyType));
      setDutyTypes(duties);
    });

    const unsubscribeRosters = onSnapshot(rostersRef, (snapshot) => {
      const rosterData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Roster));
      setRosters(rosterData);
    });

    const termsRef = collection(db, `schools/${schoolId}/terms`);
    const unsubscribeTerms = onSnapshot(termsRef, (snapshot) => {
      const termsList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          number: data.number,
          title: data.title,
          openingDate: data.openingDate ? new Date(data.openingDate) : undefined,
          closingDate: data.closingDate ? new Date(data.closingDate) : undefined,
        } as Term;
      });
      
      // Sort by term number
      setTerms(termsList.sort((a, b) => a.number - b.number));
    });

    return () => {
      unsubscribeDuties();
      unsubscribeRosters();
      unsubscribeTerms();
    };
  }, [schoolId]);

  useEffect(() => {
    if (terms.length > 0 && !newRoster.termId) {
      const now = new Date();
      const currentTerm = terms.find(t => 
        t.openingDate && t.closingDate && 
        now >= t.openingDate && now <= t.closingDate
      ) || terms[0];

      if (currentTerm) {
        setNewRoster(prev => ({
          ...prev,
          termId: currentTerm.id,
          termName: currentTerm.title,
          startDate: currentTerm.openingDate?.toISOString().split('T')[0],
          endDate: currentTerm.closingDate?.toISOString().split('T')[0]
        }));
      }
    }
  }, [terms]);

  useEffect(() => {
    if (currentRoster) {
      const updated = rosters.find(r => r.id === currentRoster.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(currentRoster)) {
        setCurrentRoster(updated);
      }
    }
  }, [rosters]);

  const loadSampleData = () => {
    const sampleTeachers: Teacher[] = [
      { id: '1', fullName: 'John Smith', shortName: 'JS', staffId: 'T001', department: 'Mathematics', availability: 'available', preferredDaysOff: [], dutyCount: 0 },
      { id: '2', fullName: 'Mary Johnson', shortName: 'MJ', staffId: 'T002', department: 'English', availability: 'available', preferredDaysOff: ['Friday'], dutyCount: 0 },
      { id: '3', fullName: 'Robert Williams', shortName: 'RW', staffId: 'T003', department: 'Science', availability: 'available', preferredDaysOff: [], dutyCount: 0 },
      { id: '4', fullName: 'Sarah Brown', shortName: 'SB', staffId: 'T004', department: 'History', availability: 'available', preferredDaysOff: [], dutyCount: 0 },
      { id: '5', fullName: 'David Miller', shortName: 'DM', staffId: 'T005', department: 'Physical Education', availability: 'unavailable', preferredDaysOff: [], dutyCount: 0 },
      { id: '6', fullName: 'Jennifer Davis', shortName: 'JD', staffId: 'T006', department: 'Art', availability: 'available', preferredDaysOff: ['Monday'], dutyCount: 0 },
      { id: '7', fullName: 'Michael Wilson', shortName: 'MW', staffId: 'T007', department: 'Music', availability: 'available', preferredDaysOff: [], dutyCount: 0 },
      { id: '8', fullName: 'Emily Taylor', shortName: 'ET', staffId: 'T008', department: 'Science', availability: 'available', preferredDaysOff: [], dutyCount: 0 },
    ];

    const sampleDuties: DutyType[] = [
      { id: '1', name: 'Morning Gate Duty', description: 'Supervise student arrival at main gate', requiredTeachers: 2, applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], frequency: 'daily', hasShifts: false },
      { id: '2', name: 'Playground Duty', description: 'Monitor students during break time', requiredTeachers: 3, applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], frequency: 'daily', hasShifts: false },
      { id: '3', name: 'Corridor Supervision', description: 'Patrol hallways between classes', requiredTeachers: 2, applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], frequency: 'daily', hasShifts: false },
      { id: '4', name: 'Canteen Duty', description: 'Supervise lunch area', requiredTeachers: 2, applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], frequency: 'daily', hasShifts: false },
      { id: '5', name: 'Bus Duty', description: 'Assist with student dismissal and bus loading', requiredTeachers: 2, applicableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], frequency: 'daily', hasShifts: false },
      { id: '6', name: 'Assembly Supervision', description: 'Manage students during morning assembly', requiredTeachers: 4, applicableDays: ['Monday', 'Friday'], frequency: 'daily', hasShifts: false },
    ];

    setTeachers(sampleTeachers);
    setDutyTypes(sampleDuties);
    toast({ title: "Sample Data Loaded", description: "8 teachers and 6 duty types have been added." });
  };


  const updateTeacher = async () => {
    if (!editingTeacher || !schoolId) return;
    try {
      const { id, ...data } = editingTeacher;
      await updateDoc(doc(db, `schools/${schoolId}/teachers`, id), data as any);
      setEditingTeacher(null);
      toast({ title: "Teacher Updated" });
    } catch (error) {
      console.error("Error updating teacher:", error);
      toast({ title: "Error", description: "Failed to update teacher.", variant: "destructive" });
    }
  };

  const deleteTeacher = async (id: string) => {
    if (!schoolId) return;
    try {
      await deleteDoc(doc(db, `schools/${schoolId}/teachers`, id));
      toast({ title: "Teacher Deleted" });
    } catch (error) {
      console.error("Error deleting teacher:", error);
      toast({ title: "Error", description: "Failed to delete teacher.", variant: "destructive" });
    }
  };

  const addDuty = async () => {
    if (!schoolId) {
      toast({ title: "Error", description: "Not authenticated or school not found.", variant: "destructive" });
      return;
    }
    if (!newDuty.name) {
      toast({ title: "Error", description: "Duty name is required.", variant: "destructive" });
      return;
    }

    try {
      const dutyData = {
        name: newDuty.name,
        description: newDuty.description || '',
        requiredTeachers: newDuty.requiredTeachers || 1,
        applicableDays: newDuty.applicableDays || DAYS_OF_WEEK,
        frequency: newDuty.frequency || 'daily',
        hasShifts: newDuty.hasShifts || false
      };

      await addDoc(collection(db, `schools/${schoolId}/dutyTypes`), dutyData);
      
      setNewDuty({ 
        requiredTeachers: 1, 
        applicableDays: DAYS_OF_WEEK,
        frequency: 'daily',
        hasShifts: false
      });
      setShowDutyDialog(false);
      toast({ title: "Duty Type Added", description: `${dutyData.name} has been added.` });
    } catch (error) {
      console.error("Error adding duty:", error);
      toast({ title: "Error", description: "Failed to save duty to database.", variant: "destructive" });
    }
  };

  const updateDuty = async () => {
    if (!editingDuty || !schoolId) return;
    try {
      const { id, ...data } = editingDuty;
      await updateDoc(doc(db, `schools/${schoolId}/dutyTypes`, id), data as any);
      setEditingDuty(null);
      toast({ title: "Duty Updated" });
    } catch (error) {
      console.error("Error updating duty:", error);
      toast({ title: "Error", description: "Failed to update duty.", variant: "destructive" });
    }
  };

  const deleteDuty = async (id: string) => {
    if (!schoolId) return;
    try {
      await deleteDoc(doc(db, `schools/${schoolId}/dutyTypes`, id));
      toast({ title: "Duty Deleted" });
    } catch (error) {
      console.error("Error deleting duty:", error);
      toast({ title: "Error", description: "Failed to delete duty.", variant: "destructive" });
    }
  };

  const createRoster = async () => {
    if (!schoolId) {
      toast({ title: "Error", description: "School not identified.", variant: "destructive" });
      return;
    }
    if (!newRoster.name || !newRoster.startDate || !newRoster.endDate) {
      toast({ title: "Error", description: "Name, start date, and end date are required.", variant: "destructive" });
      return;
    }

    try {
      const rosterData = {
        name: newRoster.name,
        startDate: newRoster.startDate,
        endDate: newRoster.endDate,
        format: newRoster.format || 'weekly',
        termId: newRoster.termId,
        termName: newRoster.termName,
        assignments: [],
        status: 'draft',
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, `schools/${schoolId}/rosters`), rosterData);
      const rosterWithId = { ...rosterData, id: docRef.id } as Roster;

      setCurrentRoster(rosterWithId);
      setNewRoster({ format: 'weekly', status: 'draft' });
      setShowRosterDialog(false);
      setActiveTab('editor');
      toast({ title: "Roster Created", description: `${rosterWithId.name} is ready for editing.` });
    } catch (error) {
      console.error("Error creating roster:", error);
      toast({ title: "Error", description: "Failed to create roster.", variant: "destructive" });
    }
  };

  const saveAssignment = async (dutyId: string, day: string, teacherIds: string[], notes?: string, shift?: 'day' | 'night') => {
    if (!currentRoster || !schoolId) return;

    // Save current state for undo
    setUndoStack([...undoStack, currentRoster.assignments]);
    setRedoStack([]);

    const duty = dutyTypes.find(d => d.id === dutyId);
    if (!duty) return;

    let newAssignments = [...currentRoster.assignments];

    if (duty.frequency === 'weekly' || duty.frequency === 'monthly') {
      // Apply to all applicable days for this duty
      duty.applicableDays.forEach(applicableDay => {
        const existingIdx = newAssignments.findIndex(
          a => a.dutyId === dutyId && a.day === applicableDay && a.shift === shift
        );
        
        if (existingIdx >= 0) {
          newAssignments[existingIdx] = { ...newAssignments[existingIdx], teacherIds, notes, shift };
        } else {
          newAssignments.push({ dutyId, day: applicableDay, teacherIds, notes, shift });
        }
      });
    } else {
      // Daily: only apply to the specific day
      const existingIndex = newAssignments.findIndex(
        a => a.dutyId === dutyId && a.day === day && a.shift === shift
      );

      if (existingIndex >= 0) {
        newAssignments[existingIndex] = { dutyId, day, teacherIds, notes, shift };
      } else {
        newAssignments.push({ dutyId, day, teacherIds, notes, shift });
      }
    }

    try {
      await updateDoc(doc(db, `schools/${schoolId}/rosters`, currentRoster.id), {
        assignments: newAssignments
      });
      
      const updatedRoster = { ...currentRoster, assignments: newAssignments };
      setCurrentRoster(updatedRoster);

      if (duty.frequency !== 'daily') {
        toast({ 
          title: "Assignment Propagated", 
          description: `This ${duty.frequency} duty has been assigned for all applicable days.` 
        });
      }

      // Update teacher duty counts (local only for performance, will refresh on load)
      const updatedTeachers = teachers.map(t => ({
        ...t,
        dutyCount: newAssignments.filter(a => a.teacherIds.includes(t.id)).length
      }));
      setTeachers(updatedTeachers);
    } catch (error) {
      console.error("Error saving assignment:", error);
      toast({ title: "Error", description: "Failed to save assignment.", variant: "destructive" });
    }
  };

  const undo = () => {
    if (undoStack.length === 0 || !currentRoster) return;
    const previousState = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, currentRoster.assignments]);
    setUndoStack(undoStack.slice(0, -1));
    const updatedRoster = { ...currentRoster, assignments: previousState };
    setCurrentRoster(updatedRoster);
    setRosters(rosters.map(r => r.id === currentRoster.id ? updatedRoster : r));
  };

  const redo = () => {
    if (redoStack.length === 0 || !currentRoster) return;
    const nextState = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, currentRoster.assignments]);
    setRedoStack(redoStack.slice(0, -1));
    const updatedRoster = { ...currentRoster, assignments: nextState };
    setCurrentRoster(updatedRoster);
    setRosters(rosters.map(r => r.id === currentRoster.id ? updatedRoster : r));
  };

  const getAssignment = (dutyId: string, day: string, shift?: 'day' | 'night'): DutyAssignment | undefined => {
    return currentRoster?.assignments.find(a => a.dutyId === dutyId && a.day === day && a.shift === shift);
  };

  const getTeacherById = (id: string) => teachers.find(t => t.id === id);

  const hasConflict = (teacherId: string, day: string, excludeDutyId?: string, shift?: 'day' | 'night'): boolean => {
    if (!currentRoster) return false;
    return currentRoster.assignments.some(a => 
      a.day === day && 
      a.dutyId !== excludeDutyId && 
      a.teacherIds.includes(teacherId) &&
      (shift === undefined || a.shift === undefined || a.shift === shift)
    );
  };

  const publishRoster = async () => {
    if (!currentRoster || !schoolId) return;
    try {
      await updateDoc(doc(db, `schools/${schoolId}/rosters`, currentRoster.id), {
        status: 'published'
      });
      setCurrentRoster({ ...currentRoster, status: 'published' });
      toast({ title: "Roster Published", description: "The roster is now final." });
    } catch (error) {
      console.error("Error publishing roster:", error);
      toast({ title: "Error", description: "Failed to publish roster.", variant: "destructive" });
    }
  };

  const exportToPDF = () => {
    toast({ title: "Export Started", description: "Generating PDF... (Demo feature)" });
  };

  const exportToCSV = () => {
    if (!currentRoster) return;
    
    let csv = "Duty,Day,Shift,Teachers,Notes\n";
    dutyTypes.forEach(duty => {
      DAYS_OF_WEEK.filter(d => duty.applicableDays.includes(d)).forEach(day => {
        if (duty.hasShifts) {
          // Add Shift 1
          const dayAssignment = getAssignment(duty.id, day, 'day');
          const dayTeachers = dayAssignment?.teacherIds.map(id => getTeacherById(id)?.shortName).join('; ') || '';
          csv += `"${duty.name}","${day}","Shift 1","${dayTeachers}","${dayAssignment?.notes || ''}"\n`;

          // Add Shift 2
          const nightAssignment = getAssignment(duty.id, day, 'night');
          const nightTeachers = nightAssignment?.teacherIds.map(id => getTeacherById(id)?.shortName).join('; ') || '';
          csv += `"${duty.name}","${day}","Shift 2","${nightTeachers}","${nightAssignment?.notes || ''}"\n`;
        } else {
          const assignment = getAssignment(duty.id, day);
          const teacherNames = assignment?.teacherIds.map(id => getTeacherById(id)?.shortName).join('; ') || '';
          csv += `"${duty.name}","${day}","N/A","${teacherNames}","${assignment?.notes || ''}"\n`;
        }
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentRoster.name.replace(/\s+/g, '_')}.csv`;
    a.click();
    toast({ title: "CSV Exported" });
  };


  const handleNextPage = () => {
      if (teachers.length < itemsPerPage) return;
      if (lastVisible) {
          setPageStack(prev => [...prev, lastVisible]);
          setCurrentPage(prev => prev + 1);
      }
  };

  const handlePrevPage = () => {
      if (currentPage > 1) {
          setPageStack(prev => prev.slice(0, -1));
          setCurrentPage(prev => prev - 1);
      }
  };

  const estimatedTotalPages = Math.ceil(totalTeachers / itemsPerPage);

  const filteredTeachers = teachers; // Search is handled server-side now

  const getTeacherDutiesForRoster = (teacherId: string): (DutyAssignment & { duty: DutyType | undefined })[] => {
    if (!currentRoster) return [];
    return currentRoster.assignments
      .filter(a => a.teacherIds.includes(teacherId))
      .map(a => ({
        ...a,
        duty: dutyTypes.find(d => d.id === a.dutyId)
      }));
  };

  const EditorCell = ({ duty, day, shift }: { duty: DutyType, day: string, shift?: 'day' | 'night' }) => {
    const isApplicable = duty.applicableDays.includes(day);
    const assignment = getAssignment(duty.id, day, shift);
    const assignedTeachers = assignment?.teacherIds.map(id => getTeacherById(id)).filter(Boolean) || [];
    const isFull = assignedTeachers.length >= duty.requiredTeachers;

    return (
      <td className="p-1 text-center border-r last:border-r-0">
        {isApplicable ? (
          <Button
            variant="ghost"
            className={`w-full h-auto min-h-[60px] p-2 flex flex-col items-center justify-center gap-1 transition-all hover:scale-[1.02] ${
              isFull ? 'bg-green-50/50 dark:bg-green-900/20 border-green-100 dark:border-green-900/30' : 
              assignedTeachers.length > 0 ? 'bg-orange-50/50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-900/30' : 
              'hover:bg-muted/50'
            }`}
            onClick={() => {
              setAssignmentContext({ dutyId: duty.id, day, shift });
              setShowAssignDialog(true);
            }}
          >
            {assignedTeachers.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-1">
                {assignedTeachers.map(t => (
                  <Badge key={t!.id} variant="secondary" className="text-[10px] px-1 h-4">
                    {t!.shortName}
                  </Badge>
                ))}
              </div>
            ) : (
              <Plus className="h-3 w-3 text-muted-foreground/40" />
            )}
            {!isFull && assignedTeachers.length > 0 && (
              <span className="text-[9px] font-medium text-orange-600 dark:text-orange-400">
                +{duty.requiredTeachers - assignedTeachers.length}
              </span>
            )}
          </Button>
        ) : (
          <div className="h-[60px] bg-muted/20 flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground/30 font-medium">N/A</span>
          </div>
        )}
      </td>
    );
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 min-w-0 h-full">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
            <div className="max-w-[1600px] mx-auto space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Duty Roster Maker</h1>
                  <p className="text-muted-foreground">Create and manage teacher duty rosters</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadSampleData}>
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Load Sample Data
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full">
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="teachers">Teachers</TabsTrigger>
                  <TabsTrigger value="duties">Duties</TabsTrigger>
                  <TabsTrigger value="rosters">Rosters</TabsTrigger>
                  <TabsTrigger value="editor" disabled={!currentRoster}>Editor</TabsTrigger>
                </TabsList>

                {/* Dashboard Tab */}
                <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{teachers.length}</p>
                    <p className="text-sm text-muted-foreground">Teachers</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dutyTypes.length}</p>
                    <p className="text-sm text-muted-foreground">Duty Types</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{rosters.length}</p>
                    <p className="text-sm text-muted-foreground">Rosters</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{rosters.filter(r => r.status === 'published').length}</p>
                    <p className="text-sm text-muted-foreground">Published</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button className="w-full justify-start" variant="outline" onClick={() => { setShowDutyDialog(true); setActiveTab('duties'); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add New Duty Type
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setShowRosterDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Create New Roster
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Teacher Workload</CardTitle>
                <CardDescription>Duty distribution across teachers</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-48">
                  {teachers.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No teachers added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {teachers.sort((a, b) => b.dutyCount - a.dutyCount).slice(0, 8).map(teacher => (
                        <div key={teacher.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{teacher.shortName}</span>
                            <Badge variant={teacher.availability === 'available' ? 'default' : teacher.availability === 'unavailable' ? 'destructive' : 'secondary'} className="text-xs">
                              {teacher.availability}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">{teacher.dutyCount} duties</span>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {currentRoster && (
            <Card className="bg-card border-primary/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5" />
                  Currently Editing: {currentRoster.name}
                </CardTitle>
                <CardDescription>
                  {currentRoster.startDate} to {currentRoster.endDate} • 
                  <Badge variant={currentRoster.status === 'published' ? 'default' : 'secondary'} className="ml-2">
                    {currentRoster.status}
                  </Badge>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => setActiveTab('editor')}>
                  <Edit2 className="h-4 w-4 mr-2" /> Continue Editing
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Teachers Tab */}
        <TabsContent value="teachers" className="space-y-4">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center gap-4 bg-background/50 p-3 rounded-lg border border-border/50 shadow-sm mb-3 backdrop-blur-sm">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, ID, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none focus-visible:ring-0 text-base placeholder:text-muted-foreground/70 h-8"
              />
            </div>

            <div className="rounded-xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="hover:bg-transparent border-b border-border/50">
                    <TableHead className="w-[70px] h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Initials</TableHead>
                    <TableHead className="h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Full Name</TableHead>
                    <TableHead className="h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Staff ID</TableHead>
                    <TableHead className="h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Department</TableHead>
                    <TableHead className="h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Availability</TableHead>
                    <TableHead className="h-10 text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Workload</TableHead>
                    <TableHead className="h-10 text-right text-xs uppercase tracking-wider font-semibold text-muted-foreground/80">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeachers.map((teacher, index) => (
                    <TableRow 
                      key={teacher.id} 
                      className={`
                        hover:bg-muted/50 transition-colors cursor-pointer group border-b border-border/40
                        ${index % 2 === 0 ? 'bg-white/50 dark:bg-transparent' : 'bg-muted/10'}
                      `}
                      onClick={() => setEditingTeacher(teacher)}
                    >
                      <TableCell className="py-2">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 border border-indigo-200/50 dark:border-indigo-800/30 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold text-xs shadow-sm group-hover:scale-105 transition-transform">
                          {teacher.shortName.substring(0, 2).toUpperCase()}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-foreground py-2 text-sm">
                        {teacher.fullName}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs py-2">
                        {teacher.staffId || <span className="opacity-50">-</span>}
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-1.5">
                          {teacher.department ? (
                            <Badge variant="secondary" className="font-normal text-xs px-2 py-0.5 bg-secondary/40 text-secondary-foreground hover:bg-secondary/50">
                              {teacher.department}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs italic">General</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className={`
                          inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border
                          ${teacher.availability === 'available' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50' : ''}
                          ${teacher.availability === 'unavailable' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900/50' : ''}
                          ${teacher.availability === 'preferred-off' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50' : ''}
                        `}>
                          <span className={`w-1 h-1 rounded-full mr-1.5 
                            ${teacher.availability === 'available' ? 'bg-emerald-500' : ''}
                            ${teacher.availability === 'unavailable' ? 'bg-red-500' : ''}
                            ${teacher.availability === 'preferred-off' ? 'bg-amber-500' : ''}
                          `} />
                          {teacher.availability === 'preferred-off' ? 'Preferred Off' : teacher.availability.charAt(0).toUpperCase() + teacher.availability.slice(1)}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex items-center gap-3">
                           <div className="w-20 h-1.5 bg-secondary/50 rounded-full overflow-hidden">
                             <div 
                                className={`h-full rounded-full transition-all duration-500 ${
                                  teacher.dutyCount > 5 ? 'bg-red-500' : 
                                  teacher.dutyCount > 3 ? 'bg-amber-500' : 
                                  'bg-blue-500'
                                }`} 
                                style={{ width: `${Math.min((teacher.dutyCount / 8) * 100, 100)}%` }} 
                             />
                           </div>
                           <span className="text-xs font-mono font-medium text-muted-foreground w-6 text-right">{teacher.dutyCount}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={(e) => { e.stopPropagation(); setEditingTeacher(teacher); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={(e) => { e.stopPropagation(); deleteTeacher(teacher.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B] mb-4">
              <div className="text-sm text-muted-foreground text-center">
                Page {currentPage} {totalTeachers > 0 ? `of ~${estimatedTotalPages}` : ''} ({teachers.length} shown)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
                  {currentPage}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={teachers.length < itemsPerPage}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Edit Teacher Dialog */}
          <Dialog open={!!editingTeacher} onOpenChange={() => setEditingTeacher(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Teacher</DialogTitle>
              </DialogHeader>
              {editingTeacher && (
                <div className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input 
                      value={editingTeacher.fullName} 
                      onChange={(e) => setEditingTeacher({...editingTeacher, fullName: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Short Name</Label>
                    <Input 
                      value={editingTeacher.shortName} 
                      onChange={(e) => setEditingTeacher({...editingTeacher, shortName: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input 
                      value={editingTeacher.department || ''} 
                      onChange={(e) => setEditingTeacher({...editingTeacher, department: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Availability</Label>
                    <Select 
                      value={editingTeacher.availability} 
                      onValueChange={(v: 'available' | 'unavailable' | 'preferred-off') => setEditingTeacher({...editingTeacher, availability: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="unavailable">Unavailable</SelectItem>
                        <SelectItem value="preferred-off">Preferred Days Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={updateTeacher} className="w-full">Update Teacher</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Duties Tab */}
        <TabsContent value="duties" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Duty Types & Locations</h2>
            <Dialog open={showDutyDialog} onOpenChange={setShowDutyDialog}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" /> Add Duty Type</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Duty Type</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Duty Name *</Label>
                    <Input 
                      value={newDuty.name || ''} 
                      onChange={(e) => setNewDuty({...newDuty, name: e.target.value})}
                      placeholder="Morning Gate Duty"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={newDuty.description || ''} 
                      onChange={(e) => setNewDuty({...newDuty, description: e.target.value})}
                      placeholder="Supervise student arrival..."
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-xl bg-blue-50/30 dark:bg-blue-900/10 border-blue-100/50 dark:border-blue-900/20 m-2 transition-all hover:bg-blue-50/50 dark:hover:bg-blue-900/20 shadow-sm">
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-blue-900 dark:text-blue-100">Shift Basis</Label>
                      <p className="text-[11px] text-blue-700/70 dark:text-blue-300/60 leading-tight">Create separate assignment slots for multiple shifts</p>
                    </div>
                    <Checkbox 
                      checked={newDuty.hasShifts}
                      onCheckedChange={(checked) => setNewDuty({...newDuty, hasShifts: !!checked})}
                      className="border-blue-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </div>
                  <div>
                    <Label>Required Teachers</Label>
                    <Input 
                      type="number"
                      min={1}
                      value={newDuty.requiredTeachers || 1} 
                      onChange={(e) => setNewDuty({...newDuty, requiredTeachers: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Assignment Frequency</Label>
                    <Select 
                      value={newDuty.frequency || 'daily'} 
                      onValueChange={(value: 'daily' | 'weekly' | 'monthly') => setNewDuty({...newDuty, frequency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily Rotation (Changes daily)</SelectItem>
                        <SelectItem value="weekly">Weekly Rotation (Changes weekly)</SelectItem>
                        <SelectItem value="monthly">Monthly Rotation (Changes monthly)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Applicable Days</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {DAYS_OF_WEEK.map(day => (
                        <label key={day} className="flex items-center gap-1 text-sm">
                          <Checkbox 
                            checked={newDuty.applicableDays?.includes(day)}
                            onCheckedChange={(checked) => {
                              const days = newDuty.applicableDays || [];
                              setNewDuty({
                                ...newDuty, 
                                applicableDays: checked ? [...days, day] : days.filter(d => d !== day)
                              });
                            }}
                          />
                          {day.slice(0, 3)}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button onClick={addDuty} className="w-full mt-2">Add Duty Type</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dutyTypes.map(duty => (
              <Card key={duty.id} className="bg-card">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{duty.name}</h3>
                      {duty.description && <p className="text-sm text-muted-foreground mt-1">{duty.description}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge>{duty.requiredTeachers} teacher{duty.requiredTeachers > 1 ? 's' : ''}</Badge>
                      <Badge variant="secondary" className="text-[10px] capitalize font-normal">
                        {duty.frequency} Rotation
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {duty.applicableDays.map(day => (
                      <Badge key={day} variant="outline" className="text-xs">{day.slice(0, 3)}</Badge>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingDuty(duty)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteDuty(duty.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Edit Duty Dialog */}
          <Dialog open={!!editingDuty} onOpenChange={() => setEditingDuty(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Duty Type</DialogTitle>
              </DialogHeader>
              {editingDuty && (
                <div className="space-y-4">
                  <div>
                    <Label>Duty Name</Label>
                    <Input 
                      value={editingDuty.name} 
                      onChange={(e) => setEditingDuty({...editingDuty, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={editingDuty.description} 
                      onChange={(e) => setEditingDuty({...editingDuty, description: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-xl bg-blue-50/30 dark:bg-blue-900/10 border-blue-100/50 dark:border-blue-900/20 m-2 transition-all hover:bg-blue-50/50 dark:hover:bg-blue-900/20 shadow-sm">
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-blue-900 dark:text-blue-100">Shift Basis</Label>
                      <p className="text-[11px] text-blue-700/70 dark:text-blue-300/60 leading-tight">Create separate assignment slots for multiple shifts</p>
                    </div>
                    <Checkbox 
                      checked={editingDuty.hasShifts}
                      onCheckedChange={(checked) => setEditingDuty({...editingDuty, hasShifts: !!checked})}
                      className="border-blue-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </div>
                  <div>
                    <Label>Required Teachers</Label>
                    <Input 
                      type="number"
                      min={1}
                      value={editingDuty.requiredTeachers} 
                      onChange={(e) => setEditingDuty({...editingDuty, requiredTeachers: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Assignment Frequency</Label>
                    <Select 
                      value={editingDuty.frequency} 
                      onValueChange={(value: 'daily' | 'weekly' | 'monthly') => setEditingDuty({...editingDuty, frequency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily Rotation (Changes daily)</SelectItem>
                        <SelectItem value="weekly">Weekly Rotation (Changes weekly)</SelectItem>
                        <SelectItem value="monthly">Monthly Rotation (Changes monthly)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={updateDuty} className="w-full mt-2">Update Duty</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Rosters Tab */}
        <TabsContent value="rosters" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Saved Rosters</h2>
            <Dialog open={showRosterDialog} onOpenChange={setShowRosterDialog}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" /> Create Roster</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Roster</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Roster Name *</Label>
                    <Input 
                      value={newRoster.name || ''} 
                      onChange={(e) => setNewRoster({...newRoster, name: e.target.value})}
                      placeholder="Term 1 2026"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date *</Label>
                      <Input 
                        type="date"
                        value={newRoster.startDate || ''} 
                        onChange={(e) => setNewRoster({...newRoster, startDate: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>End Date *</Label>
                      <Input 
                        type="date"
                        value={newRoster.endDate || ''} 
                        onChange={(e) => setNewRoster({...newRoster, endDate: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Format</Label>
                    <Select 
                      value={newRoster.format} 
                      onValueChange={(v: 'weekly' | 'daily') => setNewRoster({...newRoster, format: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly (Repeating Pattern)</SelectItem>
                        <SelectItem value="daily">Daily (Custom per Day)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Academic Term</Label>
                    <Select 
                      value={newRoster.termId || ''} 
                      onValueChange={(v) => {
                        const term = terms.find(t => t.id === v);
                        setNewRoster({
                          ...newRoster, 
                          termId: v, 
                          termName: term?.title,
                          startDate: term?.openingDate?.toISOString().split('T')[0],
                          endDate: term?.closingDate?.toISOString().split('T')[0]
                        });
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select term" /></SelectTrigger>
                      <SelectContent>
                        {terms.map(term => (
                          <SelectItem key={term.id} value={term.id}>{term.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={createRoster} className="w-full mt-2">Create Roster</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {rosters.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">No Rosters Yet</h3>
                <p className="text-muted-foreground mb-4">Create your first duty roster to get started</p>
                <Button onClick={() => setShowRosterDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Create Roster
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rosters.map(roster => (
                <Card key={roster.id} className={`bg-card ${currentRoster?.id === roster.id ? 'border-primary' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{roster.name}</h3>
                        <p className="text-sm text-muted-foreground">{roster.startDate} to {roster.endDate}</p>
                        <p className="text-sm text-muted-foreground">{roster.format} format • {roster.assignments.length} assignments</p>
                      </div>
                      <Badge variant={roster.status === 'published' ? 'default' : 'secondary'}>
                        {roster.status}
                      </Badge>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" onClick={() => { setCurrentRoster(roster); setActiveTab('editor'); }}>
                        <Edit2 className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setCurrentRoster(roster); exportToCSV(); }}>
                        <Download className="h-4 w-4 mr-1" /> Export
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRosters(rosters.filter(r => r.id !== roster.id))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Editor Tab */}
        <TabsContent value="editor" className="space-y-4">
          {currentRoster && (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-lg font-semibold">{currentRoster.name}</h2>
                  <p className="text-sm text-muted-foreground">{currentRoster.startDate} to {currentRoster.endDate}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={undo} disabled={undoStack.length === 0}>
                    <RotateCcw className="h-4 w-4 mr-1" /> Undo
                  </Button>
                  <Button variant="outline" size="sm" onClick={redo} disabled={redoStack.length === 0}>
                    <RotateCw className="h-4 w-4 mr-1" /> Redo
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportToCSV}>
                    <Download className="h-4 w-4 mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportToPDF}>
                    <FileText className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" onClick={publishRoster} disabled={currentRoster.status === 'published'}>
                    <CheckCircle className="h-4 w-4 mr-1" /> Publish
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Roster Grid */}
                <div className="lg:col-span-3">
                  <Card className="bg-card overflow-x-auto">
                    <CardContent className="p-4">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 font-medium">Duty / Time</th>
                            {DAYS_OF_WEEK.slice(0, 5).map(day => (
                              <th key={day} className="text-center p-2 font-medium">{day.slice(0, 3)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dutyTypes.map(duty => (
                            <React.Fragment key={duty.id}>
                              {duty.hasShifts ? (
                                <>
                                  <tr className="border-b bg-blue-50/20 dark:bg-blue-900/10">
                                    <td className="p-2 border-r">
                                      <div className="font-medium text-sm">{duty.name} <Badge variant="outline" className="text-[10px] ml-1">Shift 1</Badge></div>
                                    </td>
                                    {DAYS_OF_WEEK.slice(0, 5).map(day => (
                                      <EditorCell key={day} duty={duty} day={day} shift="day" />
                                    ))}
                                  </tr>
                                  <tr className="border-b bg-indigo-50/20 dark:bg-indigo-900/10">
                                    <td className="p-2 border-r">
                                      <div className="font-medium text-sm">{duty.name} <Badge variant="outline" className="text-[10px] ml-1">Shift 2</Badge></div>
                                    </td>
                                    {DAYS_OF_WEEK.slice(0, 5).map(day => (
                                      <EditorCell key={day} duty={duty} day={day} shift="night" />
                                    ))}
                                  </tr>
                                </>
                              ) : (
                                <tr className="border-b">
                                  <td className="p-2 border-r">
                                    <div className="font-medium text-sm">{duty.name}</div>
                                  </td>
                                  {DAYS_OF_WEEK.slice(0, 5).map(day => (
                                    <EditorCell key={day} duty={duty} day={day} />
                                  ))}
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar Stats */}
                <div className="space-y-4">
                  <Card className="bg-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Teacher Workload</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-64">
                        <div className="space-y-2">
                          {teachers.filter(t => t.availability === 'available').map(teacher => {
                            const duties = getTeacherDutiesForRoster(teacher.id);
                            return (
                              <div 
                                key={teacher.id} 
                                className={`p-2 rounded cursor-pointer transition-colors ${selectedTeacher === teacher.id ? 'bg-primary/10' : 'hover:bg-muted'}`}
                                onClick={() => setSelectedTeacher(selectedTeacher === teacher.id ? null : teacher.id)}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-sm">{teacher.shortName}</span>
                                  <Badge variant={duties.length > 3 ? 'destructive' : duties.length > 1 ? 'default' : 'secondary'}>
                                    {duties.length}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {selectedTeacher && (
                    <Card className="bg-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          {getTeacherById(selectedTeacher)?.fullName}'s Duties
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-48">
                          {getTeacherDutiesForRoster(selectedTeacher).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No duties assigned</p>
                          ) : (
                            <div className="space-y-2">
                              {getTeacherDutiesForRoster(selectedTeacher).map((item, idx) => (
                                <div key={idx} className="p-2 bg-muted/50 rounded text-sm">
                                  <div className="font-medium">{item.duty?.name}</div>
                                  <div className="text-muted-foreground">{item.day} {item.shift ? `• ${item.shift === 'day' ? 'Shift 1' : 'Shift 2'}` : ''}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Assignment Dialog */}
              <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      Assign Teachers
                      {assignmentContext?.shift && (
                        <Badge variant="outline" className="capitalize">{assignmentContext.shift === 'day' ? 'Shift 1' : 'Shift 2'}</Badge>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  {assignmentContext && (
                    <div className="space-y-4">
                      <div>
                        <p className="font-medium">{dutyTypes.find(d => d.id === assignmentContext.dutyId)?.name}</p>
                        <p className="text-sm text-muted-foreground">{assignmentContext.day}</p>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search teachers..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <ScrollArea className="h-64">
                        <div className="space-y-2">
                          {filteredTeachers.filter(t => t.availability === 'available').map(teacher => {
                            const currentAssignment = getAssignment(assignmentContext.dutyId, assignmentContext.day, assignmentContext.shift);
                            const isAssigned = currentAssignment?.teacherIds.includes(teacher.id) || false;
                            const hasConflictNow = hasConflict(teacher.id, assignmentContext.day, assignmentContext.dutyId, assignmentContext.shift);

                            return (
                              <div 
                                key={teacher.id} 
                                className={`p-3 rounded border cursor-pointer transition-colors ${isAssigned ? 'bg-primary/10 border-primary' : hasConflictNow ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300' : 'hover:bg-muted'}`}
                                onClick={() => {
                                  const current = currentAssignment?.teacherIds || [];
                                  const newTeacherIds = isAssigned 
                                    ? current.filter(id => id !== teacher.id)
                                    : [...current, teacher.id];
                                  saveAssignment(assignmentContext.dutyId, assignmentContext.day, newTeacherIds, currentAssignment?.notes, assignmentContext.shift);
                                }}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span className="font-medium">{teacher.fullName}</span>
                                    <span className="text-muted-foreground ml-2">({teacher.shortName})</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {hasConflictNow && (
                                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                                    )}
                                    <Checkbox checked={isAssigned} />
                                  </div>
                                </div>
                                {hasConflictNow && (
                                  <p className="text-xs text-orange-600 mt-1">Already assigned on {assignmentContext.day}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                      <Button onClick={() => setShowAssignDialog(false)} className="w-full">Done</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}
        </TabsContent>
      </Tabs>
            </div>
          )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default DutyRosterMaker;